import React from 'react';

import {
  PlatformModelName,
  useDeletePlatformModelsMutation,
  usePlatformCountsQuery,
} from '@dashboard/generated/wp/administrator';
import { Button, TableCell, TableRow } from '@mui/material';
import { alertError, alertSuccess } from '@wp-react/notifications';

type Props = {
  name: PlatformModelName;
  platformId: string;
};

type ModelRowInfo = {
  label: React.ReactNode;
  count: number | undefined;
};

function useModelRowInfo({ name, platformId }: Props): ModelRowInfo {
  const { data } = usePlatformCountsQuery({ variables: { id: platformId } });
  switch (name) {
    case PlatformModelName.ApiKey:
      return { label: 'API Keys', count: data?.Platform.apiKeysCount };
    case PlatformModelName.Contact:
      return { label: 'Contacts', count: data?.Platform.contactsCount };
    case PlatformModelName.ContactsGroup:
      return { label: 'Contact Groups', count: data?.Platform.contactsGroupCount };
    case PlatformModelName.ContactsGroupContact:
      return { label: 'Contact Group Contacts', count: data?.Platform.contactsGroupContactsCount };
    case PlatformModelName.DomainNameVerification:
      return { label: 'Domain Name Verifications', count: data?.Platform.domainNameVerificationsCount };
    case PlatformModelName.EmailMessage:
      return { label: 'Email Messages', count: data?.Platform.emailMessagesCount };
    case PlatformModelName.EmailMessagesBatch:
      return { label: 'Email Message Batches', count: data?.Platform.emailMessagesBatchesCount };
    case PlatformModelName.PlatformUser:
      return { label: 'Platform Users', count: data?.Platform.platformUsersCount };
    case PlatformModelName.RequestLog:
      return { label: 'Request Logs', count: data?.Platform.requestLogsCount };
    case PlatformModelName.Template:
      return { label: 'Templates', count: data?.Platform.templatesCount };
    case PlatformModelName.TemplateImage:
      return { label: 'Template Images', count: data?.Platform.templateImagesCount };
    case PlatformModelName.TimelineLogItem:
      return { label: 'Timeline Log Items', count: data?.Platform.timelineLogItemsCount };
    case PlatformModelName.VerifiedEmailAddress:
      return { label: 'Verified Email Addresses', count: data?.Platform.verifiedEmailAddressesCount };
    case PlatformModelName.Workflow:
      return { label: 'Workflows', count: data?.Platform.workflowsCount };
    case PlatformModelName.WorkflowRun:
      return { label: 'Workflow Runs', count: data?.Platform.workflowRunsCount };
    case PlatformModelName.WorkflowRunCommand:
      return { label: 'Workflow Run Commands', count: data?.Platform.workflowRunCommandsCount };
  }
}

export default function ModelCountRow({ name, platformId }: Props) {
  const { label, count } = useModelRowInfo({ name, platformId });
  const [execMutation, { loading }] = useDeletePlatformModelsMutation();
  const { refetch } = usePlatformCountsQuery({ variables: { id: platformId } });

  const onClick: React.MouseEventHandler = async (ev) => {
    ev.preventDefault();
    const confirmed = window.confirm(
      `Are you sure you want to delete all ${label} records? This action cannot be undone.`
    );
    if (!confirmed) {
      return;
    }
    try {
      await execMutation({ variables: { id: platformId, model: name } });
      await refetch();
      alertSuccess('Models were deleted successfully.');
    } catch (e) {
      console.error(e);
      alertError('Error deleting the models.');
    }
  };

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell align="right">{count ?? '...'}</TableCell>
      <TableCell>
        <Button onClick={onClick} variant="outlined" size="small" disabled={loading}>
          Delete
        </Button>
      </TableCell>
    </TableRow>
  );
}
